/**
 * Students API - Parent-Reported Absence
 *
 * POST /v1/students/report-absence
 *
 * Mirrors apps/backend/src/api/students/v1/use-cases/report-absence/schema.ts.
 *
 * A parent-authed, collection route (NO path param — targets come solely from
 * the body `students[]`) that performs a DIRECT Blackbaud attendance write on
 * behalf of one or more of the caller's own children. Plan:
 * docs/_archive/2026-10-17-plans-sweep/plans/2026-07-08-004-feat-parent-reported-absence-backend-endpoint-plan.md
 *
 * Three Types mirror the BB parent portal:
 *  - ALL_DAY: single date or a date range (`end_date`), span capped at 14 days.
 *  - LATE_ARRIVAL: single date + arrival `time`.
 *  - EARLY_DISMISSAL: single date + dismissal `time` + `returning` flag
 *    (comment-only — does not bound meeting selection, KTD3).
 *
 * `dry_run` resolves the full plan (children, meetings, reason, composed
 * comments) and echoes it WITHOUT writing to Blackbaud.
 */

import { z } from 'zod'

export const ReportAbsenceTypeSchema = z.enum(['ALL_DAY', 'LATE_ARRIVAL', 'EARLY_DISMISSAL'])
export type ReportAbsenceType = z.infer<typeof ReportAbsenceTypeSchema>

/** Abuse / BB-quota safety caps (KTD3 Request bounds). Tunable, not load-bearing on the wire shape. */
export const REPORT_ABSENCE_MAX_STUDENTS = 10
export const REPORT_ABSENCE_MAX_SPAN_DAYS = 14
export const REPORT_ABSENCE_MAX_COMMENT_LENGTH = 300

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^\d{2}:\d{2}(:\d{2})?$/

const ReportAbsenceBaseSchema = z.object({
  students: z
    .array(z.string().min(1))
    .min(1, 'At least one student is required')
    .max(REPORT_ABSENCE_MAX_STUDENTS, `At most ${REPORT_ABSENCE_MAX_STUDENTS} students per submission`),
  type: ReportAbsenceTypeSchema,
  start_date: z.string().regex(DATE_RE, 'start_date must be YYYY-MM-DD'),
  end_date: z.string().regex(DATE_RE, 'end_date must be YYYY-MM-DD').optional(),
  time: z.string().regex(TIME_RE, 'time must be HH:mm or HH:mm:ss').optional(),
  returning: z.boolean().optional(),
  reason_id: z.string().min(1, 'reason_id is required'),
  comment: z.string().min(1, 'comment is required').max(REPORT_ABSENCE_MAX_COMMENT_LENGTH),
  dry_run: z.boolean().optional().default(false)
})

type ReportAbsenceBaseData = z.infer<typeof ReportAbsenceBaseSchema>

/** ALL_DAY: no time/returning; an optional end_date range capped at 14 days. */
function validateAllDayFields(data: ReportAbsenceBaseData, ctx: z.RefinementCtx): void {
  if (data.time !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['time'], message: 'time is not allowed for ALL_DAY' })
  }
  if (data.returning !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['returning'], message: 'returning is not allowed for ALL_DAY' })
  }
  if (data.end_date === undefined) return

  if (data.end_date < data.start_date) {
    ctx.addIssue({ code: 'custom', path: ['end_date'], message: 'end_date must be on or after start_date' })
    return
  }
  const spanDays = Math.round(
    (new Date(`${data.end_date}T00:00:00Z`).getTime() - new Date(`${data.start_date}T00:00:00Z`).getTime()) / 86_400_000
  )
  if (spanDays > REPORT_ABSENCE_MAX_SPAN_DAYS) {
    ctx.addIssue({
      code: 'custom',
      path: ['end_date'],
      message: `Date range cannot exceed ${REPORT_ABSENCE_MAX_SPAN_DAYS} days`
    })
  }
}

/** LATE_ARRIVAL / EARLY_DISMISSAL: single-date only, time required, returning required only for EARLY_DISMISSAL. */
function validateTimedFields(data: ReportAbsenceBaseData, ctx: z.RefinementCtx): void {
  if (data.end_date !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['end_date'], message: 'end_date is only allowed for ALL_DAY' })
  }
  if (data.time === undefined) {
    ctx.addIssue({ code: 'custom', path: ['time'], message: 'time is required for this type' })
  }
  if (data.type === 'EARLY_DISMISSAL' && data.returning === undefined) {
    ctx.addIssue({ code: 'custom', path: ['returning'], message: 'returning is required for EARLY_DISMISSAL' })
  }
  if (data.type === 'LATE_ARRIVAL' && data.returning !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['returning'], message: 'returning is not allowed for LATE_ARRIVAL' })
  }
}

export const ReportAbsenceRequestSchema = ReportAbsenceBaseSchema.superRefine((data, ctx) => {
  if (data.type === 'ALL_DAY') {
    validateAllDayFields(data, ctx)
  } else {
    validateTimedFields(data, ctx)
  }
})

export type ReportAbsenceRequest = z.infer<typeof ReportAbsenceRequestSchema>

/**
 * POST /v1/students/report-absence-admin — the school-admin operator variant
 * (the web attendance tester's "Parent report absence" mode). Same body as the
 * parent-authed request PLUS `reported_by_id` naming the parent the record is
 * attributed to. The write is performed under that parent's Blackbaud id
 * (forced server-side, never spoofable). The RESPONSE is byte-identical to the
 * parent endpoint (reuse `ReportAbsenceResponse` below).
 *
 * `ReportAbsenceRequestSchema` carries a `superRefine`, so it cannot be
 * `.extend`ed — an intersection preserves those cross-field refinements.
 */
export const ReportAbsenceAdminRequestSchema = z.intersection(
  ReportAbsenceRequestSchema,
  z.object({
    reported_by_id: z.string().min(1, 'reported_by_id is required').describe('The parent school-user public ID (CUID)')
  })
)
export type ReportAbsenceAdminRequest = z.infer<typeof ReportAbsenceAdminRequestSchema>

/**
 * What Blackbaud is VERIFIED to hold for a meeting after the write (SCH-1023).
 *
 * Blackbaud answers both attendance write paths with a bare 200 whether or not a
 * record was created, so `written` alone cannot be trusted — an advance-dated
 * absence returns 200 and leaves nothing that survives to the day. Clients that
 * tell a parent their report is in should key on `ATTACHED`, not on `written`.
 *
 *  - `ATTACHED`     Blackbaud returns a record on that meeting.
 *  - `NOT_ATTACHED` the read succeeded and there is no record. The write did not land.
 *  - `UNVERIFIED`   the check could not run. Genuinely unknown — not a verdict either way.
 */
export const ReportAbsenceAttachmentSchema = z.enum(['ATTACHED', 'NOT_ATTACHED', 'UNVERIFIED'])
export type ReportAbsenceAttachment = z.infer<typeof ReportAbsenceAttachmentSchema>

/** Per-meeting write outcome (one row per selected meeting). */
export const ReportAbsenceMeetingResultSchema = z.object({
  student_id: z.string().describe("The student's public ID (CUID)"),
  day: z.string().describe('YYYY-MM-DD, the meeting date'),
  section_platform_id: z.number().int().describe('Blackbaud section id'),
  meeting_start_time: z.string().describe('School-local wall-clock, offset stripped'),
  excuse_type_platform_id: z.number().int().describe('Blackbaud excuse type id written'),
  comment: z.string().describe('The composed, [mobile]-marked comment'),
  written: z
    .boolean()
    .describe(
      'Blackbaud ACCEPTED the write (false in dry-run, or when this meeting write failed). Not evidence a record exists — read `attachment`.'
    ),
  attachment: ReportAbsenceAttachmentSchema.describe('What Blackbaud is verified to actually hold for this meeting'),
  error: z.string().optional().describe('Present when written is false due to a per-meeting failure')
})
export type ReportAbsenceMeetingResult = z.infer<typeof ReportAbsenceMeetingResultSchema>

/**
 * A meeting a PRIOR [mobile] submission for this (student, day) covered that
 * the current re-submission no longer covers (KTD7). SURFACE-ONLY: the endpoint
 * does NOT auto-clear these — a clear can't be re-verified against the meeting's
 * current Blackbaud record, so auto-firing risks wiping a staff edit. They are
 * returned for the client / office to reconcile (no BB write is performed).
 */
export const ReportAbsenceSupersededMeetingSchema = z.object({
  student_id: z.string(),
  day: z.string(),
  section_platform_id: z.number().int(),
  meeting_start_time: z.string()
})
export type ReportAbsenceSupersededMeeting = z.infer<typeof ReportAbsenceSupersededMeetingSchema>

/**
 * A student whose Blackbaud meeting resolution FAILED. Best-effort: the rest of
 * the submission still processes (KTD6). Distinct from a per-meeting write
 * failure (which appears in `results` with `written: false`).
 */
export const ReportAbsenceResolutionErrorSchema = z.object({
  student_id: z.string().describe("The student's public ID (CUID)"),
  error: z.string().describe('Generic upstream-failure reason (never leaks student identifiers)')
})
export type ReportAbsenceResolutionError = z.infer<typeof ReportAbsenceResolutionErrorSchema>

/**
 * A requested (student, day) that resolved to ZERO meetings — surfaced so the
 * caller can distinguish "nothing scheduled that day" from a real write.
 */
export const ReportAbsenceNoMeetingsSchema = z.object({
  student_id: z.string().describe("The student's public ID (CUID)"),
  day: z.string().describe('YYYY-MM-DD')
})
export type ReportAbsenceNoMeetings = z.infer<typeof ReportAbsenceNoMeetingsSchema>

export const ReportAbsenceResponseDataSchema = z.object({
  dry_run: z.boolean(),
  reported_by_platform_id: z
    .number()
    .int()
    .describe("The parent's own Blackbaud platform id (forced, never client-set)"),
  results: z.array(ReportAbsenceMeetingResultSchema),
  superseded_meetings: z.array(ReportAbsenceSupersededMeetingSchema),
  resolution_errors: z.array(ReportAbsenceResolutionErrorSchema),
  no_meetings: z.array(ReportAbsenceNoMeetingsSchema),
  summary: z.object({
    students_count: z.number().int().min(0),
    // Meetings Blackbaud ACCEPTED the write for. Not proof a record exists.
    meetings_written: z.number().int().min(0),
    // Of those, the ones Blackbaud is VERIFIED to hold (SCH-1023). Prefer this
    // when reporting success to a person; `meetings_written` can count a write
    // Blackbaud answered 200 to and did not keep.
    meetings_attached: z.number().int().min(0),
    meetings_failed: z.number().int().min(0),
    meetings_superseded: z.number().int().min(0),
    students_resolution_failed: z.number().int().min(0),
    days_no_meetings: z.number().int().min(0)
  })
})
export type ReportAbsenceResponseData = z.infer<typeof ReportAbsenceResponseDataSchema>

export const ReportAbsenceResponseSchema = z.object({
  message: z.string(),
  data: ReportAbsenceResponseDataSchema
})
export type ReportAbsenceResponse = z.infer<typeof ReportAbsenceResponseSchema>
